export type CmtsComplaintErrorCode =
  | "duplicate_conflicting_record"
  | "cross_tenant_mismatch"
  | "sod_blocked"
  | "approval_blocked"
  | "readiness_blocked"
  | "schedule_blocked"
  | "terminal_state_conflict"
  | "stale_snapshot"
  | "missing_entity"
  | "missing_route_param"
  | "missing_actor"
  | "missing_tenant_context"
  | "unsupported_action_code"
  | "missing_action_code"
  | "failed_validation"
  | "invalid_payload"
  | "evidence_rejected"
  | "file_type_rejected"
  | "file_size_rejected"
  | "upload_failed"
  | "download_token_failed"
  | "download_blob_failed"
  | "side_effect_failed"
  | "side_effect_unavailable"
  | "mapper_failure"
  | "adapter_failure"
  | "helper_failure"
  | "unknown_status"
  | "network_unavailable"
  | "backend_route_unavailable"
  | "forbidden";

export const cmtsComplaintErrorMessages: Readonly<Record<CmtsComplaintErrorCode, string>> = {
  duplicate_conflicting_record: "A complaint with the same business key already exists for this tenant.",
  cross_tenant_mismatch: "The selected record does not belong to the active tenant scope.",
  sod_blocked: "Segregation of duties blocked this action. A different authorized user must complete this step.",
  approval_blocked: "Approval rules blocked this complaint action.",
  readiness_blocked: "Readiness checks blocked this complaint action.",
  schedule_blocked: "The action is blocked by schedule or calendar policy.",
  terminal_state_conflict: "This complaint is already in a terminal state and cannot perform the selected action.",
  stale_snapshot: "This record was updated by another user. Refresh and try again.",
  missing_entity: "The requested complaint lifecycle record was not found.",
  missing_route_param: "A required route parameter is missing for this complaint page.",
  missing_actor: "An authenticated actor is required before this complaint action can run.",
  missing_tenant_context: "Tenant context is required before this complaint action can run.",
  unsupported_action_code: "The selected workflow action is not supported by the backend action registry.",
  missing_action_code: "The workflow action code is missing.",
  failed_validation: "Backend validation rejected this complaint lifecycle payload.",
  invalid_payload: "The complaint lifecycle payload is invalid.",
  evidence_rejected: "Evidence was rejected by backend validation. Review the file type, size, confidentiality, or scan result.",
  file_type_rejected: "The selected file type is not allowed for complaint evidence.",
  file_size_rejected: "The selected file exceeds the allowed evidence upload size.",
  upload_failed: "Evidence upload failed. No successful upload was recorded.",
  download_token_failed: "The evidence download token could not be generated.",
  download_blob_failed: "The evidence file could not be downloaded.",
  side_effect_failed: "The backend action completed with a side-effect failure.",
  side_effect_unavailable: "The requested backend side effect is unavailable.",
  mapper_failure: "A complaint lifecycle mapper failed while preparing data for the UI.",
  adapter_failure: "A complaint lifecycle adapter failed while calling backend APIs.",
  helper_failure: "A complaint lifecycle helper failed while processing this action.",
  unknown_status: "The backend returned an unknown complaint status or lifecycle state.",
  network_unavailable: "The complaint backend is unavailable or the network request failed.",
  backend_route_unavailable: "The configured backend route is unavailable in the current API contract.",
  forbidden: "You are not authorized to perform this complaint lifecycle action.",
};

export function canonicalComplaintErrorMessage(code: string | undefined, fallback: string): string {
  const normalized = normalizeComplaintErrorCode(code);
  return normalized ? cmtsComplaintErrorMessages[normalized] : fallback;
}

export function normalizeComplaintErrorCode(code: string | undefined): CmtsComplaintErrorCode | undefined {
  if (!code) return undefined;
  const normalized = code.trim().toLowerCase().replace(/^cmts_err_/, "").replace(/^cmts_/, "").replace(/-/g, "_");
  if (normalized === "conflict" || normalized === "duplicate_business_key") return "duplicate_conflicting_record";
  if (normalized === "rbac_forbidden" || normalized === "unauthorized" || normalized === "forbidden") return "forbidden";
  if (normalized === "network" || normalized === "request_timeout") return "network_unavailable";
  if (normalized === "backend_route_unavailable") return "backend_route_unavailable";
  return Object.prototype.hasOwnProperty.call(cmtsComplaintErrorMessages, normalized) ? normalized as CmtsComplaintErrorCode : undefined;
}
